import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { HiCheck, HiOutlineDocumentDownload, HiOutlineEmojiSad, HiOutlineLocationMarker } from 'react-icons/hi';
import { MdNavigateBefore } from 'react-icons/md';
import OrderService from '../service/OrderService';
import { saveBlobResponse } from '../util/downloadFile';
import '../css/checkout.css'
import { formatRupee } from '../components/Price';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import { ORDER_STEPS, isOffTrack, reachedStepIndex, statusLabel, statusTone } from '../util/orderStatus';

import brand, { whatsappLink } from '../brand';

const SUPPORT_PHONE = brand.contact.phoneDisplay
const SUPPORT_WHATSAPP = whatsappLink()

const formatDate = (value) => {
    if (!value) return ''
    const date = new Date(value)
    return isNaN(date) ? '' : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const ViewYourOrder = () => {
    const { id } = useParams();
    const routerHistory = useHistory();

    const [orderData, setOrderData] = useState({})
    const [history, setHistory] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState('')

    const loadOrder = () => {
        setIsLoading(true)
        setLoadError(false)
        OrderService.getYourOrderV2(id).then(res => {
            if (res.status) {
                setOrderData(res.data.data)
                setHistory(res.data.data.history || [])
                setIsLoading(false)
            }
        }).catch(error => {
            console.log(error)
            setLoadError(true)
            setIsLoading(false)
        });
    }

    useEffect(() => {
        loadOrder()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const downloadPdf = () => {
        setIsDownloading(true)
        setDownloadError('')
        OrderService.downloadBill(id).then(res => {
            setIsDownloading(false)
            saveBlobResponse(res, "invoice-" + orderData.orderId + ".pdf")
        }).catch(error => {
            console.log(error)
            setIsDownloading(false)
            setDownloadError('The invoice is not available yet. Please try again later.')
        });
    }

    if (isLoading) {
        return (
            <main className='mv-page mv-order-detail' aria-busy='true'>
                <Skeleton width='220px' height={30} />
                <Skeleton height={96} radius='14px' style={{ marginTop: 20 }} />
                <Skeleton height={200} radius='14px' style={{ marginTop: 16 }} />
            </main>
        )
    }

    if (loadError) {
        return (
            <main className='mv-page'>
                <EmptyState icon={<HiOutlineEmojiSad />} title='We could not load this order'
                    text='Please check your connection and try again.' actionLabel='Try again' onAction={loadOrder} />
            </main>
        )
    }

    const latest = history.length > 0 ? history[history.length - 1].status : null
    const reached = reachedStepIndex(history)
    const offTrack = latest && isOffTrack(latest)
    const items = orderData.itemInfo || []

    return (
        <main className='mv-page mv-order-detail'>
            <Link to='/orders' className='mv-back-link'><MdNavigateBefore size={20} /> My orders</Link>

            <header className='mv-order-head'>
                <div>
                    <h1 className='mv-list-title'>Order {orderData.orderId}</h1>
                    {orderData.createdAt && <span className='mv-list-count'>Placed on {formatDate(orderData.createdAt)}</span>}
                </div>
                {latest && <span className={`mv-status mv-status-${statusTone(latest)}`}>{statusLabel(latest)}</span>}
            </header>

            {!offTrack && reached >= 0 &&
                <section className='mv-panel'>
                    <ol className='mv-tracker' aria-label='Order progress'>
                        {ORDER_STEPS.map((step, i) =>
                            <li key={step} className={i <= reached ? 'done' : ''} aria-current={i === reached ? 'step' : undefined}>
                                <span className='mv-tracker-dot'>{i <= reached ? <HiCheck /> : null}</span>
                                <span className='mv-tracker-label'>{statusLabel(step)}</span>
                            </li>
                        )}
                    </ol>
                </section>
            }

            <div className='mv-order-grid'>
                <section className='mv-panel'>
                    <h2 className='mv-panel-title'>Items ({items.length})</h2>
                    <ul className='mv-review-list'>
                        {items.map((item, index) =>
                            <li key={index} className='mv-review-item'>
                                <img src={item.itemImage} alt='' loading='lazy' />
                                <div className='mv-review-info'>
                                    <Link to={'/shop/items/view/' + item.id} className='mv-cart-name'>{item.itemName}</Link>
                                    <span className='mv-cart-size'>{item.size ? `Size: ${item.size} · ` : ''}Qty: {item.quantity}</span>
                                </div>
                                <span className='mv-review-price'>{formatRupee(item.finalPrice)}</span>
                            </li>
                        )}
                    </ul>
                    {(Number(orderData.deliveryCharges) > 0 || orderData.couponFreeDelivery) &&
                        <div className='mv-summary-row mv-order-extra'>
                            <span>Delivery</span>
                            <span>{orderData.couponFreeDelivery ? <span className='mv-summary-free'>Free (coupon)</span> : formatRupee(orderData.deliveryCharges)}</span>
                        </div>}
                    {Number(orderData.couponDiscount) > 0 &&
                        <div className='mv-summary-row mv-order-extra mv-order-coupon'>
                            <span>Coupon{orderData.couponCode && <span className='mv-coupon-code mv-coupon-code-sm'>{orderData.couponCode}</span>}</span>
                            <span>−{formatRupee(orderData.couponDiscount)}</span>
                        </div>}
                    {orderData.totalPrice != null &&
                        <div className='mv-summary-row mv-summary-total mv-order-total'>
                            <span>Order total</span>
                            <span>{formatRupee(orderData.totalPrice)}</span>
                        </div>}
                </section>

                <div className='mv-order-side'>
                    <section className='mv-panel'>
                        <h2 className='mv-panel-title'><HiOutlineLocationMarker /> Delivery address</h2>
                        <p className='mv-address'>{orderData.deliveryAddress}</p>
                    </section>

                    {history.length > 0 &&
                        <section className='mv-panel'>
                            <h2 className='mv-panel-title'>Order history</h2>
                            <ol className='mv-timeline'>
                                {history.slice().reverse().map((e, index) =>
                                    <li key={index} className={index === 0 ? 'latest' : ''}>
                                        <span className='mv-timeline-status'>{statusLabel(e.status)}</span>
                                        <time className='mv-timeline-date'>{e.date}</time>
                                    </li>
                                )}
                            </ol>
                        </section>}

                    <section className='mv-panel mv-order-help'>
                        <button type='button' className='mv-btn mv-btn-secondary mv-btn-block' onClick={downloadPdf} disabled={isDownloading}>
                            <HiOutlineDocumentDownload /> {isDownloading ? 'Downloading…' : 'Download invoice'}
                        </button>
                        {downloadError && <div className='mv-alert mv-alert-error' role='alert'>{downloadError}</div>}
                        <a className='mv-help-link' href={SUPPORT_WHATSAPP} target='_blank' rel='noopener noreferrer'>
                            <FaWhatsapp /> Questions? WhatsApp us at {SUPPORT_PHONE}
                        </a>
                    </section>
                </div>
            </div>

            <button type='button' className='mv-link-btn mv-continue' onClick={() => routerHistory.push('/')}>
                Continue shopping
            </button>
        </main>
    )
}

export default ViewYourOrder
